import { useCallback, useEffect, useRef } from 'react';

type EmailStatus = 'idle' | 'checking' | 'exists' | 'not_found' | 'api_unavailable';
type EmailStep = 1 | 2 | 3 | 4 | 5;
type FocusLoginInputOptions = {
  requestVirtualKeyboard?: boolean;
};

type UseMobileLoginFocusParams = {
  isMobile: boolean;
  showEmailForm: boolean;
  cardMode: string;
  emailStep: EmailStep;
  setPassword: (value: string) => void;
  setError: (value: string) => void;
  setEmailStep: (value: EmailStep) => void;
  setEmailStatus: (value: EmailStatus) => void;
};

export function useMobileLoginFocus({
  isMobile,
  showEmailForm,
  cardMode,
  emailStep,
  setPassword,
  setError,
  setEmailStep,
  setEmailStatus,
}: UseMobileLoginFocusParams) {
  const mobilePasswordStepHistoryArmedRef = useRef(false);

  const focusLoginInputForKeyboard = useCallback((
    input: HTMLInputElement | null,
    options: FocusLoginInputOptions = {},
  ) => {
    if (!input) return;

    input.focus({ preventScroll: false });

    if (!isMobile) return;

    const end = input.value.length;
    try {
      input.setSelectionRange(end, end);
    } catch {
      // Tipos como email/password podem restringir selecao em alguns navegadores.
    }

    try {
      input.click();
    } catch {
      // Click programatico nao e garantido em todos os navegadores mobile.
    }

    if (options.requestVirtualKeyboard) {
      try {
        (navigator as any).virtualKeyboard?.show?.();
      } catch {
        // API experimental: quando indisponivel, seguimos com focus/click.
      }
    }

    try {
      input.scrollIntoView({ block: 'center', inline: 'nearest' });
    } catch {
      // Evita quebrar foco em navegadores com scroll restrito.
    }
  }, [isMobile]);

  useEffect(() => {
    if (!isMobile || !showEmailForm || cardMode !== 'login' || emailStep !== 2) {
      mobilePasswordStepHistoryArmedRef.current = false;
      return;
    }

    if (!mobilePasswordStepHistoryArmedRef.current) {
      window.history.pushState({ acLoginStep: 'password' }, '', window.location.href);
      mobilePasswordStepHistoryArmedRef.current = true;
    }

    const handlePasswordStepBack = () => {
      if (!mobilePasswordStepHistoryArmedRef.current) return;
      mobilePasswordStepHistoryArmedRef.current = false;
      setPassword('');
      setError('');
      setEmailStep(1);
      setEmailStatus('idle');
    };

    window.addEventListener('popstate', handlePasswordStepBack);
    return () => window.removeEventListener('popstate', handlePasswordStepBack);
  }, [isMobile, showEmailForm, cardMode, emailStep, setPassword, setError, setEmailStep, setEmailStatus]);

  const resetMobilePasswordStepHistory = useCallback(() => {
    mobilePasswordStepHistoryArmedRef.current = false;
  }, []);

  return {
    focusLoginInputForKeyboard,
    resetMobilePasswordStepHistory,
  };
}
